// src/utils/sat/satBreakdownData.js
//
// Firestore I/O for the SAT subcategory breakdown, isolated so both the
// diagnostic report component and the (upcoming) SAT plan generator load the
// same inputs the same way. Reproduces the load() effect that previously lived
// inside SubcategoryBreakdown.js.

import { db } from "../../firebaseConfig";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";

/**
 * Load the question metadata index + the student's answers for one attempt.
 *
 * @param {Object}  params
 * @param {string}  params.testId                Diagnostic test id (subcollection of questions).
 * @param {string} [params.attemptId]            diagnostic_results doc id ("<uid>_<testId>"). Preferred.
 * @param {string} [params.studentId]            Fallback doc id when no attemptId is given.
 * @param {Object} [params.answers]              Pre-loaded answers; when provided, the
 *                                               diagnostic_results read is skipped.
 * @param {string} [params.diagnosticCollection] Defaults to "sat_diagnostic_tests".
 * @returns {Promise<{ questionIndex: Map, answers: Object }>}
 */
export async function loadSatBreakdownInputs({
  testId,
  attemptId,
  studentId,
  answers: answersProp,
  diagnosticCollection = "sat_diagnostic_tests",
}) {
  const qSnap = await getDocs(
    collection(db, diagnosticCollection, testId, "questions")
  );
  const map = new Map();
  qSnap.forEach((d) => {
    const v = d.data() || {};
    const key = v.questionId || d.id;
    map.set(key, {
      questionId: key,
      subcategory: v.subcategory,
      correctAnswer: v.correctAnswer,
    });
  });

  let answers = answersProp;
  if (!answers && studentId) {
    // Use attemptId if available, otherwise studentId
    const reportRef = doc(db, "diagnostic_results", attemptId || studentId);
    const reportSnap = await getDoc(reportRef);
    answers = reportSnap.exists() ? reportSnap.data().answers || {} : {};
  }

  return { questionIndex: map, answers: answers || {} };
}
